import { GroupBranchDto, GroupDto } from "../features/organization/group.types";
import {
  GetOrganizationDto,
  GetOrganizationsDto,
  OrganizationDto,
} from "../features/organization/organization.types";

/*
 * Create a groupTree from an organization and a group array
 * The organization is the top root of the map
 */
function isGetOrganizationDto(
  organization: GetOrganizationsDto | GetOrganizationDto,
): organization is GetOrganizationDto {
  return (organization as GetOrganizationDto).organizationMembers !== undefined;
}

export function createGroupTree(
  organization: GetOrganizationsDto | GetOrganizationDto,
): GroupBranchDto {
  let groupBranch = createBranches(organization.groups);

  let branch: GroupBranchDto = {
    selectionState: 0,
    name: organization.name,
    id: organization.id,
    organizationId: organization.id,
    groups: groupBranch,
    ...(isGetOrganizationDto(organization)
      ? { groupMembers: organization.organizationMembers }
      : {}),
  };

  return branch;
}

function createBranches(groups: GroupDto[]): GroupBranchDto[] {
  const map = new Map<string, GroupBranchDto>(); // Map to track groups by their ID
  const roots: GroupBranchDto[] = []; // Array to hold the root groups

  // Create a map of groups with empty "groups" array for nesting
  groups.forEach((group) => {
    map.set(group.id, {
      selectionState: 0,
      name: group.name,
      id: group.id,
      organizationId: group.organizationId,
      groupMembers: group.groupMembers,
    });
  });

  // Link each group to its parent or mark it as a root
  groups.forEach((group) => {
    const currentGroup = map.get(group.id);
    if (!currentGroup) return;

    if (group.parentGroupId) {
      const parentGroup = map.get(group.parentGroupId);
      if (parentGroup) {
        // Ensure that parentGroup.groups is defined
        if (!parentGroup.groups) {
          parentGroup.groups = []; // Initialize groups array if undefined
        }
        parentGroup.groups.push(currentGroup); // Add the group to its parent's groups
      }
    } else {
      roots.push(currentGroup); // No parent, so this is a root node
    }
  });

  return roots; // Return the array of root-level groups
}

export function addElementToTree(
  tree: GroupBranchDto[], // Tree (list of root nodes)
  newElement: GroupBranchDto, // New element to add
  idProperty: keyof GroupBranchDto = "id", // Identifier of the node
  parentProperty: keyof GroupBranchDto = "parentGroupId", // Parent node identifier
): GroupBranchDto[] {
  const parentId = newElement[parentProperty];

  // If the new element has no parent, add it as a root node
  if (!parentId) {
    return [...tree, newElement]; // Return a new array with the new root element
  }

  // Helper function to recursively find and update the parent node
  function addElementInTree(nodes: GroupBranchDto[]): GroupBranchDto[] {
    return nodes.map((node) => {
      if (node[idProperty] === parentId) {
        // Found the parent node, return a new object with updated groups
        const updatedGroups = node.groups
          ? [...node.groups, newElement]
          : [newElement];
        return {
          ...node,
          groups: updatedGroups,
        };
      } else if (node.groups) {
        // Recursively search in child groups
        return {
          ...node,
          groups: addElementInTree(node.groups),
        };
      } else {
        return node; // No change
      }
    });
  }
  return addElementInTree(tree);
}

// Helper function to recursively find and update a node
export function updateTreeGroupElement(
  nodes: GroupBranchDto[],
  changedNodeId: string, // Parent node identifier
  updatedGroup: OrganizationDto,
): GroupBranchDto[] {
  return nodes.map((node) => {
    if (node.id === changedNodeId) {
      return {
        ...node,
        groups: node.groups,
        ...updatedGroup,
      };
    } else if (node.groups) {
      // Recursively search in child groups
      return {
        ...node,
        groups: updateTreeGroupElement(
          node.groups,
          changedNodeId,
          updatedGroup,
        ),
      };
    } else {
      return node; // No change
    }
  });
}

export function removeElementFromTree(
  tree: GroupBranchDto[], // The tree to operate on
  idToRemove: string, // The ID of the element to remove
  idProperty: keyof GroupBranchDto = "id", // The property that identifies each node
  childProperty: keyof GroupBranchDto = "groups", // The property that contains child nodes
): GroupBranchDto[] {
  function removeNode(nodes: GroupBranchDto[]): GroupBranchDto[] {
    return nodes
      .filter((node) => node[idProperty] !== idToRemove) // Remove the node if it matches the id
      .map((node) => {
        // If the node has children, recursively process them
        if (node[childProperty]) {
          return {
            ...node,
            [childProperty]: removeNode(
              node[childProperty] as GroupBranchDto[],
            ),
          };
        } else {
          return node; // Return the node as is if no children
        }
      });
  }

  return removeNode(tree);
}

export function replaceElementInTree(
  tree: GroupBranchDto[], // The tree to operate on
  updatedElement: GroupBranchDto, // The updated element to replace the existing one
  idProperty: keyof GroupBranchDto = "id", // The property that identifies each node
  childProperty: keyof GroupBranchDto = "groups", // The property that contains child nodes
): GroupBranchDto[] {
  function replaceNode(nodes: GroupBranchDto[]): GroupBranchDto[] {
    return nodes.map((node) => {
      if (node[idProperty] === updatedElement[idProperty]) {
        // Found the node to replace, return the updated element
        return { ...node, ...updatedElement };
      } else if (node[childProperty]) {
        // Recursively process the child nodes if they exist
        return {
          ...node,
          [childProperty]: replaceNode(node[childProperty] as GroupBranchDto[]),
        };
      } else {
        // Return the node unchanged if no match and no children
        return node;
      }
    });
  }

  return replaceNode(tree);
}

export interface findElementByGroupIdResponse {
  groupBranch: GroupBranchDto | null;
  trace?: string;
}

export function findElementByGroupId(
  tree: GroupBranchDto, // The tree to search in
  groupId: string, // The groupId to find
): findElementByGroupIdResponse | null {
  let trace: string = "";
  // Helper function to recursively search the tree
  function findNode(nodes: GroupBranchDto[]): GroupBranchDto | null {
    for (const node of nodes) {
      if (node.id === groupId) {
        return node; // Found the matching node
      }
      if (node.groups) {
        // Recursively search in child nodes
        trace += node.name;

        const found = findNode(node.groups);
        if (found) return found;
      }
    }
    return null; // Return null if no matching node is found
  }

  if (tree.groups) return { groupBranch: findNode(tree.groups), trace: trace };
  else return null;
}
